import { inject, Injectable, OnDestroy } from '@angular/core';
import { AmplitudeService, SentryService } from '@brighthub/logging';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import {
  ConversationalBtmAcceptedRun,
  ConversationalBtmApiService,
  ConversationalBtmConversationDetail,
  ConversationalBtmRunMessage,
  ConversationalBtmRunState,
} from '../btm';

export interface CopilotConversationInvalidation {
  clientId: string;
  conversationId: string;
}

/** BrightNav adapter over shared BTM transport plus invalidation-only tab sync. */
@Injectable({ providedIn: 'root' })
export class CopilotApiService implements OnDestroy {
  private readonly btm = inject(ConversationalBtmApiService);
  private readonly messages = inject(MessageService);
  private readonly sentry = inject(SentryService);
  private readonly amplitude = inject(AmplitudeService);
  private readonly invalidationSubject =
    new Subject<CopilotConversationInvalidation>();
  private readonly channel = createBroadcastChannel();

  readonly invalidations$ = this.invalidationSubject.asObservable();

  constructor() {
    if (this.channel) {
      this.channel.onmessage = (event) => {
        const data = event.data as {
          type?: unknown;
          payload?: Partial<CopilotConversationInvalidation>;
        };
        if (
          data.type === 'conversation-state-changed' &&
          typeof data.payload?.clientId === 'string' &&
          typeof data.payload?.conversationId === 'string'
        ) {
          this.invalidationSubject.next({
            clientId: data.payload.clientId,
            conversationId: data.payload.conversationId,
          });
        }
      };
    }
  }

  ngOnDestroy(): void {
    this.channel?.close();
    this.invalidationSubject.complete();
  }

  async fetchConversation(
    clientId: string,
    conversationId: string,
  ): Promise<ConversationalBtmConversationDetail> {
    this.sentry.addBreadcrumb({
      category: 'copilot',
      message: 'Fetching BTM conversation',
      data: {
        operation: 'fetch_conversation',
        client_guid: clientId,
        client_id: clientId,
        conversation_id: conversationId,
      },
    });
    return this.btm.getCompleteConversation(conversationId);
  }

  async submitMessage(options: {
    clientId: string;
    clientGuid: string;
    conversationId: string;
    content: string;
    idempotencyKey: string;
  }): Promise<ConversationalBtmAcceptedRun> {
    this.amplitude.logEvent('copilot_send_message', {
      subjectId: options.clientId,
      conversationId: options.conversationId,
    });
    this.sentry.addBreadcrumb({
      category: 'copilot',
      message: 'Submitting BTM message',
      data: {
        operation: 'submit_message',
        client_guid: options.clientGuid,
        client_id: options.clientId,
        conversation_id: options.conversationId,
      },
    });
    const accepted = await this.btm.submit(
      {
        conversation_id: options.conversationId,
        state: { client_guid: options.clientGuid },
        input: { type: 'message', content: options.content },
      },
      options.idempotencyKey,
    );
    if (accepted.conversation_id !== options.conversationId) {
      throw new Error(
        'The conversational API returned a different conversation id.',
      );
    }
    return accepted;
  }

  followRun(options: {
    conversationId: string;
    runId: string;
    streamUrl: string;
    signal: AbortSignal;
    initialMessages?: readonly ConversationalBtmRunMessage[];
    initialLastEventId?: string;
    onConnectionChange?: (
      status: 'connecting' | 'streaming' | 'reconnecting' | 'reconciling',
      attempt: number,
    ) => void;
    onSnapshot?: (state: ConversationalBtmRunState) => void;
  }) {
    return this.btm.followRun(options);
  }

  broadcastInvalidation(payload: CopilotConversationInvalidation): void {
    this.channel?.postMessage({
      type: 'conversation-state-changed',
      payload,
    });
  }

  reportLoadMessagesError(message: string): void {
    this.messages.add({
      severity: 'error',
      summary: 'Failed to load messages',
      detail: message,
    });
  }

  reportRunError(message: string): void {
    this.messages.add({
      severity: 'error',
      summary: 'Navi reply interrupted',
      detail: message,
    });
  }
}

function createBroadcastChannel(): BroadcastChannel | undefined {
  return typeof BroadcastChannel === 'undefined'
    ? undefined
    : new BroadcastChannel('brightnav-copilot');
}
